/**
 * Blinkit-theme cart / checkout sections, shared by BkCartView (``/cart``) and BkCheckoutView (``/checkout``) so
 * both screens show the same bill, address, payment and offers UI. They only render state and call the handlers
 * the page controllers own (useCartController / useCheckoutController) — no pricing or ordering logic lives here.
 */
import { useId } from "react";
import {
    AlertTriangle, BadgePercent, Banknote, Briefcase, ChevronRight, CreditCard, Home, Loader2, MapPin, ShieldCheck,
    Wallet, X,
} from "lucide-react";
import { fmtInr, priceBreakdown } from "../../../lib/priceBreakdown";
import { BkCard } from "../../../components/blinkit/BkUI";

const ADDR_ICON = { home: Home, work: Briefcase };

/** "Price details" — every line straight from the server totals (lib/priceBreakdown). */
export function BkPriceDetails({ totals, walletApplied = 0, testid = "bk-price-details", title = "Price details", toPayLabel = "To pay", footer = null }) {
    const { lines, total, savings } = priceBreakdown(totals);
    const toPay = Math.max(0, Math.round((total - walletApplied) * 100) / 100);
    return (
        <BkCard title={title} testid={testid}>
            <dl className="space-y-2.5 text-[13.5px]">
                {lines.map((l) => (
                    <div key={l.key} className="flex items-start justify-between gap-3" data-testid={`bk-line-${l.key}`}>
                        <dt className="min-w-0">
                            <span className="text-foreground/85">{l.label}</span>
                            {l.note && <span className="block text-[11.5px] text-muted-foreground leading-snug">{l.note}</span>}
                        </dt>
                        <dd className={`shrink-0 tabular-nums font-semibold ${l.key === "discount" && l.amount < 0 ? "text-emerald-700" : ""}`}>
                            {l.key === "delivery" && l.free ? (
                                <span className="inline-flex items-center gap-1.5">
                                    {l.struck ? <span className="text-muted-foreground line-through font-normal">{fmtInr(l.struck)}</span> : null}
                                    <span className="text-emerald-700 font-bold">FREE</span>
                                </span>
                            ) : l.key === "discount" ? (
                                l.amount < 0 ? `−${fmtInr(-l.amount)}` : fmtInr(0)
                            ) : fmtInr(l.amount)}
                        </dd>
                    </div>
                ))}
                <div className="border-t border-dashed border-border pt-3 flex items-center justify-between gap-3" data-testid="bk-total">
                    <dt className="text-[15px] font-extrabold">Total</dt>
                    <dd className="text-[17px] font-extrabold tabular-nums">{fmtInr(total)}</dd>
                </div>
                {walletApplied > 0 && (
                    <>
                        <div className="flex items-center justify-between gap-3 text-[13px]" data-testid="bk-line-wallet">
                            <dt className="text-foreground/85 inline-flex items-center gap-1.5"><Wallet size={13} className="text-primary" /> Paid from wallet</dt>
                            <dd className="tabular-nums font-semibold text-primary">−{fmtInr(walletApplied)}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3" data-testid="bk-to-pay">
                            <dt className="text-[14px] font-extrabold">{toPayLabel}</dt>
                            <dd className="text-[16px] font-extrabold tabular-nums">{fmtInr(toPay)}</dd>
                        </div>
                    </>
                )}
            </dl>
            {savings > 0 && (
                <p className="mt-3 rounded-xl bg-emerald-50 text-emerald-800 text-[12.5px] font-bold px-3 py-2" data-testid="bk-savings">
                    You save {fmtInr(savings)} on this order
                </p>
            )}
            {footer}
        </BkCard>
    );
}

/** Delivery address card — the address in use, Change, and the out-of-zone / needs-location warnings. */
export function BkAddressSection({ addr, onChange, onAdd, warning, testid = "cart-address" }) {
    const Icon = (addr && ADDR_ICON[addr.label]) || MapPin;
    return (
        <BkCard testid={testid}>
            {addr ? (
                <div className="flex items-start gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon size={19} /></span>
                    <div className="min-w-0 flex-1">
                        <p className="text-[12px] text-muted-foreground font-semibold">Delivering to</p>
                        <p className="text-[14.5px] font-extrabold capitalize leading-tight">{addr.label || "Address"}</p>
                        <p className="text-[12.5px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                            {[addr.line1, addr.line2, addr.city, addr.pincode].filter(Boolean).join(", ")}
                        </p>
                    </div>
                    <button type="button" onClick={onChange} data-testid="addr-change"
                            className="shrink-0 h-9 px-3 rounded-lg border border-primary/40 text-primary text-[12.5px] font-bold hover:bg-primary/5">Change</button>
                </div>
            ) : (
                <button type="button" onClick={onAdd} data-testid="add-first-address"
                        className="w-full h-12 rounded-xl border-2 border-dashed border-primary/40 text-primary text-[14px] font-bold inline-flex items-center justify-center gap-2">
                    <MapPin size={17} /> Add a delivery address
                </button>
            )}
            {warning}
        </BkCard>
    );
}

/** Inline warning / error inside a section or above the pay bar. */
export function BkNotice({ tone = "warn", title, body, action, onDismiss, testid }) {
    const cls = tone === "error" ? "bg-destructive/10 border-destructive/30 text-destructive" : "bg-amber-50 border-amber-300 text-amber-900";
    return (
        <div className={`mt-3 rounded-xl border p-3 flex items-start gap-2.5 ${cls}`} data-testid={testid} role={tone === "error" ? "alert" : undefined}>
            <AlertTriangle size={17} className="shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 text-[12.5px] leading-snug">
                <p className="font-bold text-[13.5px]">{title}</p>
                {body && <p className="mt-0.5 opacity-90">{body}</p>}
                {action && <div className="mt-2">{action}</div>}
            </div>
            {onDismiss && (
                <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0 w-7 h-7 -mt-1 -mr-1 rounded-full flex items-center justify-center hover:bg-black/5"><X size={15} /></button>
            )}
        </div>
    );
}

/**
 * Payment method: optional wallet toggle (an additive source) + the methods available here. ``methods`` is
 * ``[{ id, label, sub, icon, testid }]`` built by the page from what the server allows.
 */
export function BkPaymentSection({ methods, value, onChange, walletBalance, useWallet, setUseWallet, total, emptyText }) {
    const name = useId();
    const walletCovers = useWallet && walletBalance >= total;
    const walletApplied = useWallet ? Math.min(walletBalance, total) : 0;
    return (
        <BkCard title="Payment method" icon={CreditCard} testid="cart-payment">
            {walletBalance > 0 && (
                <label className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition ${useWallet ? "border-primary bg-primary/[0.05]" : "border-border"}`} data-testid="wallet-source-section">
                    <span className="w-9 h-9 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Wallet size={18} /></span>
                    <span className="min-w-0 flex-1">
                        <span className="block text-[14px] font-bold">Use wallet balance</span>
                        <span className="block text-[12px] text-muted-foreground">
                            {fmtInr(walletBalance)} available{walletBalance >= total ? " · covers this order" : ""}
                            {useWallet && <> · <span className="text-primary font-semibold" data-testid="wallet-amount">−{fmtInr(walletApplied)}</span></>}
                        </span>
                    </span>
                    <input type="checkbox" data-testid="use-wallet" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} className="w-5 h-5 accent-primary shrink-0" />
                </label>
            )}
            {!walletCovers && (
                <div className={`space-y-2 ${walletBalance > 0 ? "mt-3" : ""}`} role="radiogroup" data-testid="payment-method-picker">
                    {walletBalance > 0 && useWallet && (
                        <p className="text-[11.5px] font-bold uppercase tracking-wider text-muted-foreground">Pay the remaining {fmtInr(Math.max(0, total - walletApplied))} with</p>
                    )}
                    {methods.length === 0 ? (
                        <p className="text-[13px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">{emptyText || "No payment methods are available for this address."}</p>
                    ) : methods.map((m) => {
                        const on = value === m.id;
                        const Icon = m.icon;
                        return (
                            <label key={m.id} data-testid={m.testid}
                                   className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition ${on ? "border-primary bg-primary/[0.05]" : "border-border hover:border-foreground/25"}`}>
                                <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><Icon size={18} /></span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-[14px] font-bold">{m.label}</span>
                                    {m.sub && <span className="block text-[12px] text-muted-foreground">{m.sub}</span>}
                                </span>
                                <input type="radio" name={name} checked={on} onChange={() => onChange(m.id)} className="w-5 h-5 accent-primary shrink-0" />
                            </label>
                        );
                    })}
                </div>
            )}
        </BkCard>
    );
}

export const PAY_ICONS = { COD: Banknote, ONLINE: CreditCard, RAZORPAY: CreditCard };

/** Coupon row: applied coupon (with savings + remove) or "view coupons" + manual code entry. */
export function BkCouponSection({ appliedCode, saved, onOpenPicker, onRemove, code, setCode, onApply, applyBusy, invalid, children }) {
    return (
        <BkCard title="Offers & coupons" icon={BadgePercent} testid="rewards-section">
            {appliedCode ? (
                <div className="flex items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2.5" data-testid="coupon-applied">
                    <BadgePercent size={18} className="text-emerald-700 shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-[13.5px] font-extrabold text-emerald-800 font-mono tracking-wide">{appliedCode}</p>
                        <p className="text-[12px] text-emerald-800">{saved > 0 ? `You save ${fmtInr(saved)}` : "Applied"}</p>
                    </div>
                    <button type="button" onClick={onRemove} data-testid="remove-coupon" className="shrink-0 h-8 px-2.5 rounded-lg text-[12px] font-bold text-emerald-800 hover:bg-emerald-100">Remove</button>
                </div>
            ) : (
                <div className="space-y-2.5">
                    <button type="button" onClick={onOpenPicker} data-testid="coupon-browse"
                            className="w-full flex items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/[0.05] px-3 py-3 text-left hover:bg-primary/10 transition">
                        <span className="text-[13.5px] font-bold text-primary">View available coupons</span>
                        <ChevronRight size={17} className="text-primary shrink-0" />
                    </button>
                    <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); onApply(); }}>
                        <input data-testid="coupon-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Enter coupon code"
                               aria-label="Coupon code" maxLength={20}
                               className="flex-1 min-w-0 h-11 rounded-xl border border-border bg-muted/40 px-3 text-[13px] font-semibold uppercase tracking-wider outline-none focus:border-primary focus:bg-card" />
                        <button type="submit" data-testid="apply-coupon-btn" disabled={applyBusy || !code}
                                className="h-11 px-4 rounded-xl bg-foreground text-background text-[13px] font-bold disabled:opacity-40">
                            {applyBusy ? <Loader2 size={15} className="animate-spin" /> : "Apply"}
                        </button>
                    </form>
                    {invalid && <p className="text-[12px] text-destructive" data-testid="coupon-invalid">{invalid}</p>}
                </div>
            )}
            {children}
        </BkCard>
    );
}

/**
 * Fixed bottom "Place order" bar — above the home indicator / Android gesture bar (safe-area inset), inside the
 * shell width. From 1024px it is static: the cart / checkout views render it as the last card of the summary column
 * (it is ``fixed`` below lg, so that DOM position changes nothing on phones and tablets). Shows what will actually be charged (``amount`` null = the bill isn't loaded yet → "—") and a busy
 * state while the order / payment is in flight.
 */
export function BkPlaceOrderBar({ amount, label, disabled, busy, busyLabel, onClick, hint, testid = "place-order" }) {
    return (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full z-40 bg-card border-t border-border shadow-[0_-10px_28px_-14px_rgba(15,23,42,0.35)] lg:static lg:translate-x-0 lg:z-auto lg:!max-w-none lg:rounded-2xl lg:border lg:shadow-none lg:overflow-hidden"
             style={{ maxWidth: "var(--shell-width)", paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="bk-pay-bar">
            {hint && <div className="px-4 pt-2 text-[12px] text-muted-foreground truncate" data-testid="bk-pay-hint">{hint}</div>}
            <div className="px-4 pt-2.5 flex items-center gap-3">
                <div className="min-w-0 leading-tight">
                    <p className="text-[11.5px] text-muted-foreground font-semibold">To pay</p>
                    <p className="text-[18px] font-extrabold tabular-nums" data-testid="grand-total">{amount == null ? "—" : fmtInr(amount)}</p>
                </div>
                <button type="button" onClick={onClick} disabled={disabled || busy} data-testid={testid}
                        className="flex-1 min-w-0 h-12 rounded-xl bg-primary text-primary-foreground text-[15px] font-extrabold inline-flex items-center justify-center gap-2 px-3 active:scale-[0.99] transition disabled:opacity-50 disabled:active:scale-100">
                    {busy && <Loader2 size={18} className="animate-spin shrink-0" />}
                    <span className="truncate">{busy ? (busyLabel || "Placing order…") : label}</span>
                </button>
            </div>
        </div>
    );
}

/** Full-screen state while an online payment is open / being verified — the cart must not be touched then. */
export function BkPaymentOverlay({ phase }) {
    if (phase !== "paying" && phase !== "confirming") return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-6" data-testid="bk-payment-overlay" role="status" aria-live="polite">
            <div className="bg-card rounded-2xl p-6 w-full max-w-xs text-center shadow-2xl">
                <span className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    {phase === "confirming" ? <Loader2 size={26} className="animate-spin" /> : <ShieldCheck size={26} />}
                </span>
                <p className="mt-4 text-[16px] font-extrabold">{phase === "confirming" ? "Confirming your payment…" : "Complete your payment"}</p>
                <p className="mt-1.5 text-[13px] text-muted-foreground">
                    {phase === "confirming" ? "This takes a few seconds. Please keep the app open." : "Finish paying in the secure Razorpay window. Closing it cancels the payment — nothing is charged."}
                </p>
            </div>
        </div>
    );
}
